import React from "react";

export const MESSAGE_TIMELINE_NEAR_BOTTOM_PX = 96;

export function isNearMessageTimelineBottom(element, threshold = MESSAGE_TIMELINE_NEAR_BOTTOM_PX) {
  if (!element) return true;
  return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
}

/** Shared conversation scroll policy: follow new activity only while the reader is near the newest message. */
export function useMessageTimelineScroll(conversationKey) {
  const timelineRef = React.useRef(null);
  const shouldFollowRef = React.useRef(true);
  const initialKeyRef = React.useRef("");
  const prependSnapshotRef = React.useRef(null);

  const onScroll = React.useCallback((event) => {
    shouldFollowRef.current = isNearMessageTimelineBottom(event.currentTarget);
  }, []);

  const scrollToNewest = React.useCallback((force = false) => {
    const element = timelineRef.current;
    if (!element || (!force && !shouldFollowRef.current)) return;
    element.scrollTop = element.scrollHeight;
  }, []);

  const capturePrependPosition = React.useCallback(() => {
    const element = timelineRef.current;
    if (!element) return;
    prependSnapshotRef.current = { height: element.scrollHeight, top: element.scrollTop };
  }, []);

  const restorePrependPosition = React.useCallback(() => {
    const element = timelineRef.current;
    const snapshot = prependSnapshotRef.current;
    if (!element || !snapshot) return;
    element.scrollTop = snapshot.top + (element.scrollHeight - snapshot.height);
    prependSnapshotRef.current = null;
  }, []);

  React.useLayoutEffect(() => {
    if (initialKeyRef.current !== conversationKey) {
      initialKeyRef.current = conversationKey;
      shouldFollowRef.current = true;
      requestAnimationFrame(() => scrollToNewest(true));
      return;
    }
    if (prependSnapshotRef.current) restorePrependPosition();
    else scrollToNewest(false);
  });

  return { timelineRef, onScroll, scrollToNewest, capturePrependPosition, restorePrependPosition, shouldFollowRef };
}
